import { useCallback, useEffect, useMemo, useRef } from "react";
import AppBackService from "../services/appback";

const DISPLAY_NOTIF_TYPES = ["tapgen"];

export default function useNotifications(userInfo, notifications, setNotifications) {
  const intervalTimeoutsRef = useRef([]);

  // cancel all intervals on unmount
  useEffect(() => {
    return () => {
      for (const intervalTimeout of intervalTimeoutsRef.current) {
        clearInterval(intervalTimeout);
      }
    };
  }, []);

  const notifInterval = useCallback(
    (callback, showSnackbar, intervalTime = 10000, numIntervals = 6, keepAlive = false) => {
      let intervalCount = 0;
      const intervalTimeout = setInterval(async () => {
        const data = await AppBackService.getUserNotifications(userInfo.username);

        if (data && data.notifs?.length) {
          // On the live tap page the static tapestries a meeting's drips make
          // are not news (TT-1237): their snackbars stay quiet there; the
          // notifications still land in the tray below.
          const quiet = (notif) => notif.type === "tapgen" && window.location.pathname.startsWith("/live-tap/");
          for (const notif of data.notifs) {
            // show snackbars
            if (
              notif.type === "tapgen" &&
              notif.tap &&
              notif.action === "create" &&
              notif.msg?.toLowerCase() === "success"
            ) {
              if (!quiet(notif)) {
                showSnackbar(`Tapestry "${notif.tap.title}" created`, "success", "View", () => {
                  window.location.href = "/tapestry/" + notif.tap.tapId;
                });
              }
              callback && callback(data);
              // the tapestry arrived, so stop polling unless the caller wants to keep listening
              if (!keepAlive) {
                cancelTimeout();
              }
            } else if (notif.msg && !quiet(notif)) {
              showSnackbar(notif.msg, !notif.severity || notif.severity === "status" ? "info" : notif.severity);
            }

            // stop the notification interval if a keeper notification is received (meeting is ended)
            if (notif.type === "keeper") {
              cancelTimeout();
            }
          }

          setNotifications((notifications) => [...data.notifs, ...notifications]);
        }

        intervalCount++;
        if (intervalCount > numIntervals - 1) {
          cancelTimeout();
        }

        function cancelTimeout() {
          intervalTimeoutsRef.current = intervalTimeoutsRef.current.filter((t) => t !== intervalTimeout);
          clearInterval(intervalTimeout);
        }
      }, intervalTime);
      intervalTimeoutsRef.current.push(intervalTimeout);
    },
    [setNotifications, userInfo]
  );

  const displayNotifs = useMemo(
    () => notifications.filter((n) => DISPLAY_NOTIF_TYPES.includes(n.type)),
    [notifications]
  );

  return { notifInterval, displayNotifs };
}
